import axios from "axios";

// Busca a URL base da API a partir das variáveis de ambiente do Vite.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Validação para garantir que a variável de ambiente está definida.
if (!API_BASE_URL) {
  throw new Error(
    "A variável de ambiente VITE_API_BASE_URL não está definida. Verifique seus arquivos .env"
  );
}

const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api`,
});

// Interceptor para adicionar o token JWT às requisições
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken"); // CORREÇÃO: A chave correta é 'authToken'
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Função para tratar erros da API de forma centralizada
const handleApiError = (error) => {
  if (error.response) {
    // O servidor respondeu com um status de erro (4xx, 5xx)
    console.error(
      "API Error:",
      error.response.data,
      "Status:",
      error.response.status
    );
    // Retorna um objeto que simula a estrutura de uma resposta bem-sucedida do Axios,
    // mas com os dados do erro dentro da propriedade 'data'.
    return { data: { success: false, ...error.response.data } };
  } else if (error.request) {
    // A requisição foi feita, mas não houve resposta
    console.error("Network Error:", error.request);
    return {
      data: {
        success: false,
        error: "Não foi possível conectar ao servidor. Verifique sua conexão.",
      },
    };
  } else {
    // Erro na configuração da requisição
    console.error("Request Setup Error:", error.message);
    return {
      data: {
        success: false,
        error: "Erro ao preparar a requisição.",
      },
    };
  }
};

export const api = {
  /**
   * Realiza o login do usuário.
   * @param {string} email
   * @param {string} senha
   */
  login: async (email, senha) => {
    try {
      const response = await apiClient.post("/auth/login", { email, senha });
      // Em caso de sucesso, a resposta já tem o formato { data: { success: true, ... } }
      return response.data;
    } catch (error) {
      // Em caso de erro, handleApiError agora retorna um objeto com a propriedade 'data',
      // mas com success: false. Isso unifica o formato da resposta.
      const errorResponse = handleApiError(error);
      return errorResponse.data;
    }
  },

  /**
   * Busca o perfil do usuário autenticado.
   */
  getProfile: async () => {
    try {
      // O token é adicionado automaticamente pelo interceptor
      const response = await apiClient.get("/user/profile");
      return response.data;
    } catch (error) {
      // Se o erro for 401 ou 403, o handleApiError já trata
      return handleApiError(error);
    }
  },

  /**
   * Solicita o envio de um e-mail de redefinição de senha.
   * @param {{ email: string }} data - O e-mail do usuário.
   */
  requestPasswordReset: async (data) => {
    try {
      const response = await apiClient.post("/auth/forgot-password", data);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Redefine a senha do usuário usando um token.
   * @param {{ token: string, senha: string }} data
   */
  resetPassword: async (data) => {
    try {
      const response = await apiClient.post("/auth/reset-password", data);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Realiza o cadastro público de um novo usuário (médico, representante, etc.).
   * @param {object} data - Dados do formulário de registro.
   */
  createPublicRegistration: async (data) => {
    try {
      const response = await apiClient.post("/public/register", data);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Busca os dados de um pré-cadastro de representante pelo token.
   * @param {string} token - O token de finalização.
   */
  getRepresentativePreRegistrationByToken: async (token) => {
    try {
      const response = await apiClient.get(
        `/public/representantes/pre-cadastro?token=${token}`
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Finaliza o cadastro de um representante.
   * @param {{ token: string, senha: string }} data - Token e nova senha.
   */
  finalizeRepresentativePreRegistration: async (data) => {
    try {
      const response = await apiClient.post(
        "/public/representantes/pre-cadastro",
        data
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Busca os dados de um pré-cadastro de clínica pelo token.
   * @param {string} token - O token de finalização.
   */
  getClinicPreRegistrationByToken: async (token) => {
    try {
      const response = await apiClient.get(
        `/public/clinicas/pre-cadastro?token=${token}`
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Finaliza o cadastro de uma clínica.
   * @param {{ token: string, senha: string, responsavel_nome?: string }} data
   */
  finalizeClinicPreRegistration: async (data) => {
    try {
      const response = await apiClient.post(
        "/public/clinicas/pre-cadastro",
        data
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Busca a lista de pré-cadastros de representantes (rota de admin).
   */
  getRepresentativePreRegistrations: async () => {
    try {
      const response = await apiClient.get(
        "/admin/representantes/pre-cadastro"
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Cria um novo pré-cadastro de representante (rota de admin).
   * @param {object} data - Dados do novo representante.
   */
  createRepresentativePreRegistration: async (data) => {
    try {
      const response = await apiClient.post(
        "/admin/representantes/pre-cadastro",
        data
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Busca a lista pública de produtos.
   */
  getProducts: async () => {
    try {
      const response = await apiClient.get("/products");
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Busca a lista de todos os produtos (rota de admin).
   */
  getAdminProducts: async () => {
    try {
      const response = await apiClient.get("/admin/products");
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Cria um novo produto (rota de admin).
   * @param {object} productData - Dados do novo produto.
   */
  createProduct: async (productData) => {
    try {
      // A rota para criar produto é uma rota de admin
      const response = await apiClient.post("/admin/products", productData);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Atualiza um produto existente (rota de admin).
   * @param {string} productId - O ID do produto a ser atualizado.
   * @param {object} productData - Os novos dados do produto.
   */
  updateProduct: async (productId, productData) => {
    try {
      const response = await apiClient.put(
        `/admin/products/${productId}`,
        productData
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
  /**
   * Deleta (desativa) um produto (rota de admin).
   * @param {string} productId - O ID do produto a ser desativado.
   */
  deleteProduct: async (productId) => {
    try {
      const response = await apiClient.delete(`/admin/products/${productId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
  /**
   * Valida um cupom de desconto.
   * @param {string} codigo - O código do cupom.
   * @param {string} [produtoId] - O ID do produto para validação de contexto.
   */
  validateCoupon: async (codigo, produtoId) => {
    try {
      const response = await apiClient.post("/cupons/validate", {
        codigo,
        produtoId,
      });
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Busca a lista de pedidos do usuário autenticado.
   */
  getPedidos: async () => {
    try {
      const response = await apiClient.get("/pedidos");
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Cria um novo pedido.
   * @param {object} pedidoData - Dados do pedido.
   */
  createPedido: async (pedidoData) => {
    try {
      const response = await apiClient.post("/pedidos", pedidoData);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Faz upload de um arquivo para o backend (que o envia para o Cloudinary).
   * @param {FormData} formData - O formulário contendo o arquivo.
   */
  uploadFile: async (formData) => {
    try {
      const response = await apiClient.post("/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("API Error on uploadFile:", error.response?.data || error);
      // Lança um erro mais específico para ser capturado no componente.
      throw new Error(
        error.response?.data?.error || "Falha no upload do arquivo."
      );
    }
  },

  /**
   * Busca os dados do dashboard do admin.
   */
  getAdminDashboard: async () => {
    try {
      const response = await apiClient.get("/admin/dashboard");
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Busca os dados do dashboard do representante.
   */
  getRepresentativeDashboard: async () => {
    try {
      const response = await apiClient.get("/representante/dashboard");
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Busca os dados do dashboard do médico/clínica.
   */
  getClinicaDashboard: async () => {
    try {
      const response = await apiClient.get("/clinica/dashboard");
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Solicita um saque para a clínica.
   * @param {{ valor: number, observacao?: string }} data
   */
  requestClinicPayout: async (data) => {
    try {
      const response = await apiClient.post("/clinica/payout-request", data);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Solicita um saque para o representante.
   * @param {{ valor: number, observacao?: string }} data
   */
  requestRepresentativePayout: async (data) => {
    try {
      const response = await apiClient.post(
        "/representante/payout-request",
        data
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
  /**
   * Reenvia um convite para uma clínica (rota de representante).
   * @param {number} solicitacaoId
   */
  resendClinicInvitation: async (solicitacaoId) => {
    try {
      const response = await apiClient.post(
        `/representante/reenviar-convite/${solicitacaoId}`
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Busca a lista de todos os usuários (rota de admin).
   */
  getAdminUsers: async () => {
    try {
      const response = await apiClient.get("/admin/users");
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Atualiza os dados bancários de um usuário (rota de admin).
   * @param {number} userId
   * @param {object} data
   */
  updateUserBankingInfo: async (userId, data) => {
    try {
      const response = await apiClient.put(
        `/admin/users/${userId}/banking`,
        data
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },
  /**
   * Atualiza um usuário (rota de admin).
   * @param {number} userId
   * @param {object} userData
   */
  updateAdminUser: async (userId, userData) => {
    try {
      const response = await apiClient.put(`/admin/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Deleta (desativa) um usuário (rota de admin).
   * @param {number} userId
   */
  deleteAdminUser: async (userId) => {
    try {
      const response = await apiClient.delete(`/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  getPedidoById: async (pedidoId) => {
    try {
      const response = await apiClient.get(`/pedidos/${pedidoId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Busca a lista de todos os cupons (rota de admin).
   */
  getCoupons: async () => {
    try {
      const response = await apiClient.get("/admin/cupons");
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Cria um novo cupom (rota de admin).
   * @param {object} couponData
   */
  createCoupon: async (couponData) => {
    try {
      const response = await apiClient.post("/admin/cupons", couponData);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Atualiza um cupom (rota de admin).
   * @param {string} couponId
   * @param {object} couponData
   */
  updateCoupon: async (couponId, couponData) => {
    try {
      const response = await apiClient.put(
        `/admin/cupons/${couponId}`,
        couponData
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Deleta (desativa) um cupom (rota de admin).
   * @param {string} couponId
   */
  deleteCoupon: async (couponId) => {
    try {
      const response = await apiClient.delete(`/admin/cupons/${couponId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Busca o estoque de cupons por produto (rota de admin).
   */
  getProductCouponStock: async () => {
    try {
      const response = await apiClient.get("/admin/products/coupon-stock");
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Atualiza o estoque de cupons de um produto (rota de admin).
   * @param {string} productId
   * @param {number | null} limite
   */
  updateProductCouponStock: async (productId, limite) => {
    try {
      const response = await apiClient.put(
        `/admin/products/coupon-stock/${productId}`,
        { limite }
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Cria um pré-cadastro de clínica (rota de representante).
   * @param {object} data
   */
  createClinicPreRegistrationByRep: async (data) => {
    try {
      const response = await apiClient.post(
        "/representante/clinicas/pre-cadastro",
        data
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Busca a lista de pré-cadastros de clínicas de um representante.
   */
  getRepresentativeClinicPreRegistrations: async () => {
    try {
      const response = await apiClient.get(
        "/representante/clinicas/pre-cadastro"
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Busca todos os pedidos (rota de admin).
   */
  getAdminPedidos: async () => {
    try {
      const response = await apiClient.get("/admin/pedidos");
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Atualiza o status de um pedido (rota de admin).
   * @param {string} pedidoId
   * @param {string} status
   */
  updatePedidoStatus: async (pedidoId, status) => {
    try {
      const response = await apiClient.put(
        `/admin/pedidos/${pedidoId}/status`,
        { status }
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Busca a lista de todas as solicitações de saque (rota de admin).
   */
  getAdminPayouts: async () => {
    try {
      const response = await apiClient.get("/admin/payouts");
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Atualiza o status de uma solicitação de saque (rota de admin).
   * @param {string} payoutType - 'clinica' ou 'representante'
   * @param {number} payoutId - ID do saque
   * @param {string} status - Novo status ('paga' ou 'pendente')
   */
  updatePayoutStatus: async (payoutType, payoutId, status) => {
    try {
      const response = await apiClient.put(
        `/admin/payouts/${payoutType}/${payoutId}`,
        {
          status,
        }
      );
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Adicione aqui outras funções de API conforme necessário...
};
